/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import React, { forwardRef, useEffect, useRef, useState } from "react";
import Files from "./FilemanagerFiles";
import LeftSideTree from "./FileManagerLeftSideTree";
import SplitLayout from "@CubeComponents/html/SplitLayout/SplitLayout";
import { CircularProgress } from "@mui/material";
import useFileManagerComponent from "./useFileManagerComponent";

export interface ContextMenuItem {
  id: string;
  name: string;
  icon?: string;
  onClick?: (e: MouseEvent, payload?: unknown) => void;
}

interface FileManagerComponentProps {
  fileManagerData: any;
  allowDragAndDropUpload: boolean;
  onFileClick: Function;
  onFileSelect: Function;
  setCurrentFolder: Function;
  toolbarItems: any;
  toolbarClick: Function;
  contextMenuSettings?: {
    file: ContextMenuItem[];
    folder: ContextMenuItem[];
    layout: ContextMenuItem[];
    visible: boolean;
  };
  menuOpen: Function;
  fileColumns: any;
  setFileManagerData: Function;
  beforeSending: Function;
  onSuccess: Function;
  onFileLoad: Function;
  showFolderTree: boolean;
  path: string;
  allowViewFileOptions: boolean;
  disableDragAndDropUpload: boolean;
  preSearchText?: string;
  // Project-level ("Folders") mode: render every Space of the project as a top-level tree root.
  projectMode?: boolean;
  projectId?: string;
}

const FileManagerComponent = forwardRef(
  (
    {
      fileManagerData = {
        cwd: {},
        files: [],
      },
      allowDragAndDropUpload = false,
      onFileClick = () => {},
      onFileSelect = () => {},
      setCurrentFolder = () => {},
      toolbarItems = [],
      toolbarClick,
      contextMenuSettings = {
        file: [],
        folder: [],
        layout: [],
        visible: true,
      },
      menuOpen = () => {},
      fileColumns = [],
      setFileManagerData,
      beforeSending = () => {},
      onSuccess = () => {},
      onFileLoad = () => {},
      showFolderTree = true,
      path,
      disableDragAndDropUpload,
      preSearchText,
      projectMode = false,
      projectId,
    }: FileManagerComponentProps,
    ref,
  ) => {
    const tempRef = useRef<any>({});
    const filesRef: any = ref || tempRef;
    const treeViewRef = useRef(null);
    // Project ("Folders") mode: the shared, full-width slot the grid portals its toolbar into (above the
    // tree + grid). Held in state so the grid re-renders and portals once the slot element is mounted.
    const [toolbarSlot, setToolbarSlot] = useState<HTMLElement | null>(null);

    const {
      // State
      selectedFolder,
      setSelectedFolder,
      filesViewMode,
      tempFolder,
      refreshGrid,
      selectedFilesInIconsMode,
      setSelectedFilesInIconsMode,
      isViewRendered,
      setIsViewRendered,
      isLoaded,

      // Refs
      splitterSizeRef,

      // Handlers
      refreshFileManager,
      refreshFiles,
      navigateToFolder,
      handleSearchFiles,
      getSelectedFilesInFileManager,
      showLoading,
      hideLoading,
    } = useFileManagerComponent({
      fileManagerData,
      setFileManagerData,
      setCurrentFolder,
      beforeSending,
      onSuccess,
      showFolderTree,
      path,
      projectMode,
      projectId,
    });
    // Expose methods via ref
    useEffect(() => {
      if (filesRef.current) {
        filesRef.current.refreshFileManager = refreshFileManager;
        filesRef.current.refreshFiles = async () => {
          const selectedRows = filesRef.current?.slickGrid?.getSelectedRows() || [];
          filesRef.current._pendingSelectedRows = selectedRows;
          await refreshFiles();
        };
        filesRef.current.filesViewMode = filesViewMode;
        filesRef.current.getSelectedFilesInFileManager = () => getSelectedFilesInFileManager(filesRef);
        filesRef.current.showLoading = showLoading;
        filesRef.current.hideLoading = hideLoading;
        filesRef.current.navigateToFolder = navigateToFolder;
      }
    }, [filesRef.current, isViewRendered, beforeSending, onSuccess, filesViewMode, selectedFilesInIconsMode]);

    // The resizable tree + grid split. `filesExtraProps` lets project mode hand the grid the shared toolbar
    // slot + adapted toolbar items; single-space/popup pass nothing (grid toolbar stays hidden).
    const renderTreeAndGrid = (filesExtraProps: any = {}) => (
      <SplitLayout
        initialSizes={splitterSizeRef.current}
        gutterWidth={"1px"}
        gutterStyle={{ position: "absolute", zIndex: 50 }}
        minSizes={[20, 45]}
        style={{ display: "flex", height: "100%" }}
        showProperties={true}
      >
        {[
          showFolderTree && (
            <div
              key="folderTree"
              className="flex h-full border border-s-0 border-t-0 border-b-0 border-e-stroke_primary_light overflow-auto"
            >
              <LeftSideTree
                ref={treeViewRef}
                onFileLoad={onFileLoad}
                fileManagerData={fileManagerData}
                selectedFolder={selectedFolder}
                setSelectedFolder={setSelectedFolder}
              />
            </div>
          ),
          <div key="filesGrid" className="h-full flex">
            <Files
              ref={filesRef}
              preSearchText={preSearchText}
              setIsViewRendered={setIsViewRendered}
              isViewRendered={isViewRendered}
              filesViewMode={filesViewMode}
              setSelectedFilesInIconsMode={setSelectedFilesInIconsMode}
              selectedFilesInIconsMode={selectedFilesInIconsMode}
              allowDragAndDropUpload={allowDragAndDropUpload}
              onFileLoad={onFileLoad}
              tempFolder={tempFolder}
              beforeSending={beforeSending}
              handleSearchFiles={handleSearchFiles}
              fileColumns={fileColumns}
              menuOpen={menuOpen}
              contextMenuSettings={contextMenuSettings}
              fileManagerData={fileManagerData}
              onFileSelect={onFileSelect}
              onFileClick={onFileClick}
              selectedFolder={selectedFolder}
              setSelectedFolder={setSelectedFolder}
              disableDragAndDropUpload={disableDragAndDropUpload}
              {...filesExtraProps}
            />
          </div>,
        ].filter(Boolean)}
      </SplitLayout>
    );

    const loadingOverlay = refreshGrid && (
      <div className="absolute h-full w-full flex justify-center items-center z-20 rounded-md">
        <CircularProgress />
      </div>
    );

    const getContent = () => {
      // Adapt the file-manager toolbar items into the grid ToolBar's item shape (text->name, iconName->
      // prefixIcon, align:"right"->rightAllign; wrap JSX templates as functions). They render inside the
      // grid's own fully-wired toolbar, alongside its filter/sort/group/column-chooser controls.
      const commonToolbarItems = (toolbarItems || []).filter((item: any) => item.show);
      return (
        // Merged card: the grid's toolbar (portaled into the slot) spans the full width above the tree +
        // grid; all three share one rounded border.
        <div className="h-full flex flex-col w-full relative border border-stroke_primary_light rounded-lg overflow-hidden">
          {loadingOverlay}
          {/* Full-width slot the Files grid portals its toolbar into. */}
          <div ref={setToolbarSlot} className="w-full" />
          {renderTreeAndGrid({
            toolbarContainer: toolbarSlot,
            commonToolbar: commonToolbarItems,
            commonToolbarClickHandler: (id: string) => toolbarClick(undefined, id),
            // Grid lives inside the merged card, which owns the border — drop the grid's own outer border.
            disableOuterBorder: true,
          })}
        </div>
      );
    };

    return (
      <div className="w-full h-full items-center justify-center overflow-auto flex flex-col">
        {isLoaded ? getContent() : <CircularProgress />}
      </div>
    );
  },
);

FileManagerComponent.displayName = "FileManagerComponent";
export default FileManagerComponent;
